'use client';

import type { ICollectionPipelineStatusV3 } from '@repo/sdk';
import { Card, CardContent, Progress } from '@repo/ui';
import { CheckCircle2, ListChecks, Send } from 'lucide-react';
import type { JSX, ReactNode } from 'react';
import { useMemo } from 'react';
import { aggregatePipelineProgress } from './aggregate-pipeline-progress';

function toPercent(done: number, total: number): number {
  return total > 0 ? Math.round((done / total) * 100) : 0;
}

interface IProgressCardProps {
  icon: ReactNode;
  label: string;
  done: number;
  total: number;
  caption: string;
}

function ProgressCard({ icon, label, done, total, caption }: IProgressCardProps): JSX.Element {
  const percent = toPercent(done, total);
  return (
    <Card>
      <CardContent className="flex flex-col gap-3 pt-6">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <span className="flex size-7 items-center justify-center rounded-md bg-muted text-foreground">
            {icon}
          </span>
          {label}
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold tabular-nums">{done}</span>
          <span className="text-sm text-muted-foreground">/ {total}</span>
          <span className="ml-auto text-sm font-semibold tabular-nums">{percent}%</span>
        </div>
        <Progress value={percent} />
        <div className="text-xs text-muted-foreground">{caption}</div>
      </CardContent>
    </Card>
  );
}

export function PipelineOverview({
  items,
}: {
  items: ICollectionPipelineStatusV3[];
}): JSX.Element | null {
  const progress = useMemo(() => aggregatePipelineProgress(items), [items]);

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="mb-6 grid gap-4 sm:grid-cols-3">
      <ProgressCard
        icon={<CheckCircle2 className="size-4" />}
        label="Collections done"
        done={progress.collectionsComplete}
        total={progress.collectionsTotal}
        caption={`${progress.collectionsTotal - progress.collectionsComplete} still in progress`}
      />
      <ProgressCard
        icon={<ListChecks className="size-4" />}
        label="Clips triaged"
        done={progress.triagedDone}
        total={progress.triagedTotal}
        caption={`${progress.triagedTotal - progress.triagedDone} left to review`}
      />
      <ProgressCard
        icon={<Send className="size-4" />}
        label="Challenges published"
        done={progress.publishedDone}
        total={progress.publishedTotal}
        caption={`${progress.publishedTotal - progress.publishedDone} not published yet`}
      />
    </div>
  );
}
